<template>
  <u-modal
    :visible="visible"
    :custom-class="[
      'prompt-editor-modal',
      { 'prompt-editor-modal--fullscreen': isFullScreen }
    ]"
    :dialog-style="dialogStyle"
    vertical
    closable
    :mask-closable="false"
    @close="handleDismiss"
    @ok="handleConfirm"
  >
    <template #header>
      <div class="prompt-editor__header">
        <PromptModeSwitch v-model="mode" test-id-suffix="fullscreen-prompt" />
      </div>
    </template>

    <div
      class="prompt-editor"
      role="group"
      :aria-label="title"
      data-testid="fullscreen-prompt-editor"
    >
      <!-- The caller keeps rendering its own editor so @ / & mentions, height
           and validation behave exactly like the inline field. -->
      <div v-if="mode === 'edit'" class="prompt-editor__editor">
        <slot />
      </div>

      <div v-else class="prompt-editor__preview">
        <div
          class="markdown-body"
          data-testid="fullscreen-prompt-preview-content"
          v-html="renderedMarkdown"
        />
      </div>
    </div>

    <template #footer>
      <u-button data-testid="cancel-fullscreen-prompt-button" @click="handleCancel">{{
        $t('common.124a48309c32c98c65a3f5c830338f54')
      }}</u-button>
      <u-button
        type="primary"
        data-testid="confirm-fullscreen-prompt-button"
        @click="handleConfirm"
        >{{ $t('common.670d03777d5b44d8f57ccd3293b9bdd3') }}</u-button
      >
    </template>
  </u-modal>
</template>

<script>
import { computed, defineComponent, inject, ref, watch } from 'vue';
import md from '@/libs/markdown';
import { EDIT_DIALOG_INJECT_KEY } from '@C/EditDialog.vue';
import { toPreviewMarkdown } from '@C/PromptEditor/promptPreview';
import PromptModeSwitch from './PromptModeSwitch.vue';

export default defineComponent({
  name: 'FullscreenPromptEditor',
  components: { PromptModeSwitch },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    // Force the viewport-filling size even outside a dialog.
    fullScreen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible', 'cancel', 'ok'],
  setup(props, { emit }) {
    const mode = ref('edit');

    // Opened from inside a dialog, a 90% modal would look like a child dialog;
    // fill the viewport so the fullscreen editor is unmistakable.
    const inDialog = inject(EDIT_DIALOG_INJECT_KEY, false);
    const isFullScreen = computed(() => props.fullScreen || inDialog);

    // 90% of the viewport for page-level prompts, 100% inside dialogs.
    const dialogStyle = computed(() =>
      isFullScreen.value
        ? { width: '100vw', height: '100vh', borderRadius: 0 }
        : { width: '90vw', height: '90vh' }
    );

    // Content captured when the editor opens, so 取消 can restore it.
    const openedValue = ref('');

    // Opening starts in edit mode and remembers the current content.
    watch(
      () => props.visible,
      visible => {
        if (visible) {
          mode.value = 'edit';
          openedValue.value = props.value;
        }
      },
      { immediate: true }
    );

    // Preview only renders the current text; the stored prompt stays untouched.
    // The value can be Markdown text or rich-editor HTML, so it is normalized
    // first, and asterisk masking (meant for streamed chat output) is disabled
    // so `**bold**` next to CJK characters still renders as emphasis.
    const renderedMarkdown = computed(() =>
      md.render(toPreviewMarkdown(props.value), { maskAsterisks: false })
    );

    const close = () => emit('update:visible', false);

    // 取消 restores the content captured on open, then closes.
    const handleCancel = () => {
      emit('cancel', openedValue.value);
      close();
    };

    // 确认 keeps the edited content, which already lives in the bound field.
    const handleConfirm = () => {
      emit('ok');
      close();
    };

    // Close icon / Esc dismiss the modal and keep the edited content.
    const handleDismiss = () => close();

    return {
      dialogStyle,
      handleCancel,
      handleConfirm,
      handleDismiss,
      isFullScreen,
      mode,
      renderedMarkdown
    };
  }
});
</script>

<style lang="less" scoped>
@import '@/styles/mixins.less';

// Mode switch sits at the far left of the header.
.prompt-editor__header {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: 100%;
}

.prompt-editor {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.prompt-editor__editor,
.prompt-editor__preview {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.prompt-editor__preview {
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 4px;
  .prompt-thin-scrollbar();
}
</style>

<style lang="less">
// The modal itself never scrolls: header / footer are fixed and only the
// editor or preview body scrolls internally.
.prompt-editor-modal {
  .uui-modal {
    display: flex;
    flex-direction: column;
    margin: 0;
    overflow: hidden;
    background: var(--background-neutral-bg-color-container);
  }

  .uui-modal-header {
    flex: none;
  }

  .uui-modal-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 16px 24px;
  }

  .uui-modal-footer {
    flex: none;
    column-gap: 12px;
  }
}

// Opened from a dialog: cover the viewport exactly and drop the rounded shell.
.uui-modal-wrap.prompt-editor-modal--fullscreen {
  overflow: hidden;

  .uui-modal {
    border-radius: 0;
  }
}
</style>
